import { useState, useEffect, useMemo } from "react";

import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useGetTouchpointVersionStatusesQuery } from "hooks/api/dictionary";
import { useTouchpointVersions } from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";

const touchpointVersionApi = getTouchpointVersion();

import { DEFAULT_CLIENT_REVIEW_STATUSES } from "types";
import { TouchpointType } from "types/touchpoint";

export interface VersionPreviewData {
  html?: string;
  beeJson?: string;
  subjectLine?: string;
  preheader?: string;
  rewriterChanges?: string;
}

interface TouchpointComparisonData {
  leftVersion?: VersionPreviewData;
  rightVersion?: VersionPreviewData;
}

interface UseCompareVersionsProps {
  touchpointVersionId: string;
  isOpen: boolean;
}

export interface SelectOption {
  value: string;
  label: string;
}

export const useCompareVersions = ({ touchpointVersionId, isOpen }: UseCompareVersionsProps) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const { data: touchpointVersions } = useTouchpointVersions({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });
  const { data: touchpointVersionStatuses = [] } = useGetTouchpointVersionStatusesQuery();

  const [touchpointData, setTouchpointData] = useState<TouchpointComparisonData>({});
  const [isLoadingLeft, setIsLoadingLeft] = useState(false);
  const [isLoadingRight, setIsLoadingRight] = useState(false);
  const [selectedLeftVersionId, setSelectedLeftVersionId] = useState<string | null>(null);
  const [selectedRightVersionId, setSelectedRightVersionId] = useState<string | null>(null);

  // Get readable review statuses for versions
  const versions = useMemo(() => touchpointVersions?.items ?? [], [touchpointVersions?.items]);

  const getClientVersionStatuses = (status?: string) => {
    if (!status) {
      return DEFAULT_CLIENT_REVIEW_STATUSES;
    }

    return (
      touchpointVersionStatuses.find((el) => el.status === status) || DEFAULT_CLIENT_REVIEW_STATUSES
    );
  };

  // Generate select options from versions
  const versionSelectOptions: SelectOption[] = versions.map(({ id, status, version }) => {
    const currentClientVersionStatuses = getClientVersionStatuses(status);
    return {
      value: id,
      label: `v${version} (${currentClientVersionStatuses?.versionStatusName})`,
    };
  });

  // Filter options for left dropdown
  const leftVersionSelectOptions = versionSelectOptions.filter(
    (option) => option.value !== selectedRightVersionId,
  );

  // Filter options for right dropdown
  const rightVersionSelectOptions = versionSelectOptions.filter(
    (option) => option.value !== selectedLeftVersionId,
  );

  // Fetch touchpoint version data and return HTML/JSON for preview
  const fetchVersionData = async (versionId: string): Promise<VersionPreviewData> => {
    if (!versionId) {
      return {};
    }

    try {
      const dto = await touchpointVersionApi.getOneVersion({
        clientId: currentClient.id,
        campaignId: currentCampaign.id,
        touchpointId: currentTouchpoint.id,
        id: versionId,
      });
      const versionData = mapTouchpointVersionDTO(dto);

      if (!versionData) {
        return {};
      }

      const isEmail = versionData.type === TouchpointType.EMAIL;

      if (isEmail) {
        const emailData = versionData as EmailAttributes;
        return {
          html: versionData.finalHtml,
          beeJson: versionData.data?.jsonOutput,
          subjectLine: emailData.subjectLine,
          preheader: emailData.preheader,
          rewriterChanges: versionData.rewriterChanges,
        };
      }

      return {
        html: versionData.finalHtml,
        beeJson: versionData.data?.jsonOutput,
        rewriterChanges: versionData.rewriterChanges,
      };
    } catch (error) {
      console.error("Failed to fetch touchpoint version:", error);
      throw error;
    }
  };

  // Handle left version selection
  const handleLeftVersionChange = async (selectedOption: SelectOption) => {
    if (!selectedOption) return;

    const versionId = selectedOption.value;
    setSelectedLeftVersionId(versionId);
    setIsLoadingLeft(true);

    try {
      const data = await fetchVersionData(versionId);
      setTouchpointData((prev) => ({
        ...prev,
        leftVersion: data,
      }));
    } catch (error) {
      console.error("Failed to load version:", error);
    } finally {
      setIsLoadingLeft(false);
    }
  };

  // Handle right version selection
  const handleRightVersionChange = async (selectedOption: SelectOption) => {
    if (!selectedOption) return;

    const versionId = selectedOption.value;
    setSelectedRightVersionId(versionId);
    setIsLoadingRight(true);

    try {
      const data = await fetchVersionData(versionId);
      setTouchpointData((prev) => ({
        ...prev,
        rightVersion: data,
      }));
    } catch (error) {
      console.error("Failed to load version:", error);
    } finally {
      setIsLoadingRight(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Clear data immediately when modal opens
      setTouchpointData({});
      setSelectedRightVersionId(touchpointVersionId);

      // Find the first version that's not the right selection for the left side default
      const defaultLeftVersion = versions.find((v) => v.id !== touchpointVersionId);
      const defaultLeftVersionId = defaultLeftVersion?.id || null;
      setSelectedLeftVersionId(defaultLeftVersionId);

      setIsLoadingRight(true);
      setIsLoadingLeft(!!defaultLeftVersionId);
    } else {
      // Reset loading states when modal closes
      setIsLoadingLeft(false);
      setIsLoadingRight(false);
    }
  }, [isOpen, touchpointVersionId, versions]);

  // Load initial versions data when modal opens
  useEffect(() => {
    if (!isOpen || !touchpointVersionId) {
      return;
    }

    const loadInitialData = async () => {
      // Load right version (the passed-in touchpointVersionId)
      try {
        const rightData = await fetchVersionData(touchpointVersionId);
        setTouchpointData((prev) => ({
          ...prev,
          rightVersion: rightData,
        }));
      } catch (error) {
        console.error("Failed to load right version:", error);
      } finally {
        setIsLoadingRight(false);
      }

      // Load left version (first version that's not the right selection)
      const defaultLeftVersion = versions.find((v) => v.id !== touchpointVersionId);
      if (defaultLeftVersion) {
        try {
          const leftData = await fetchVersionData(defaultLeftVersion.id);
          setTouchpointData((prev) => ({
            ...prev,
            leftVersion: leftData,
          }));
        } catch (error) {
          console.error("Failed to load left version:", error);
        } finally {
          setIsLoadingLeft(false);
        }
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, touchpointVersionId]);

  const isEmailType = currentTouchpoint.type === TouchpointType.EMAIL;

  return {
    // State
    touchpointData,
    isLoadingLeft,
    isLoadingRight,
    selectedLeftVersionId,
    selectedRightVersionId,
    leftVersionSelectOptions,
    rightVersionSelectOptions,
    isEmailType,
    // Actions
    handleLeftVersionChange,
    handleRightVersionChange,
  };
};
